"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/tailwind/ui/dialog";
import { TASK_HELP, type TaskHelpContent } from "@/lib/checklist-task-help";

interface TaskInfoModalProps {
  taskId: string | null;
  taskText: string;
  onClose: () => void;
}

export function TaskInfoModal({ taskId, taskText, onClose }: TaskInfoModalProps) {
  const helpContent: TaskHelpContent | null = taskId ? (TASK_HELP[taskId] ?? null) : null;

  return (
    <Dialog open={!!taskId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl">{helpContent?.title ?? "Task Information"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{taskText}</DialogDescription>
        </DialogHeader>

        {helpContent ? (
          <div className="space-y-6 pt-2">
            {/* Description */}
            <div>
              <p className="text-sm leading-relaxed text-foreground">{helpContent.description}</p>
            </div>

            {/* Steps */}
            {helpContent.steps && helpContent.steps.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-foreground">How to do this</h3>
                <ol className="space-y-2">
                  {helpContent.steps.map((step, index) => (
                    <li key={`step-${step.slice(0, 20)}`} className="flex gap-3 text-sm">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                        {index + 1}
                      </span>
                      <span className="pt-0.5 text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Tips */}
            {helpContent.tips && helpContent.tips.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-foreground">Helpful tips</h3>
                <ul className="space-y-2">
                  {helpContent.tips.map((tip) => (
                    <li key={`tip-${tip.slice(0, 20)}`} className="flex gap-3 text-sm">
                      <svg
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <span className="text-muted-foreground">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Resources */}
            {helpContent.resources && helpContent.resources.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-foreground">Resources</h3>
                <ul className="space-y-2">
                  {helpContent.resources.map((resource) => (
                    <li key={resource.url ?? resource.label}>
                      {resource.url ? (
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
                        >
                          {resource.label}
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">{resource.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="py-4 text-center text-sm text-muted-foreground">
            <p>
              Detailed guidance for this task is coming soon. In the meantime, don't hesitate to reach out to your
              funeral home or trusted advisors for help with this step.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
